import './lib/js-cookie.js';
export const CALENDAR_TYPE = {priceView: 'price-view', genericView: 'generic-view', none: 'none'};

export const COMCalendarBehavior = function(superClass) {

  var _comCalendarDateCache = {};

  return class extends superClass {
    static get properties() {
      return {
        /**
         * @desc day 3-letter abbreviation
         * Used as fallback if Date.toLocaleString is not supported
         * @type String[]
         */
        _days: {
          type: Array,
          value: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        },
        /**
         * @desc day 1-letter abbreviation
         * Used as fallback if Date.toLocaleString is not supported
         * @type String[]
         */
        _shortDays: {
          type: Array,
          value: ['S', 'M', 'T', 'W', 'T', 'F', 'S']
        },
        /**
         * @desc month names
         * Used as fallback if Date.toLocaleString is not supported
         * @type String[]
         */
        _months: {
          type: Array,
          value: [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
          ]
        },
        /**
         * @desc check support for Date.toLocaleString
         * Note: Safari 9 does not support Date.toLocaleString
         * https://wiki.wdpro.wdig.com/pages/viewpage.action?title=WDPRO+Supported+Web+Browsers&spaceKey=Tech
         * @type Boolean
         */
        _useToLocaleString: {
          type: Boolean,
          value: function() {
            return this._toLocaleStringSupportsLocales();
          }
        },

        /**
         * @desc Cookie properties for locale.
         * @type Object
         */
        _localeFields: {
          type: Object,
          value: {
            cookieName: 'languageSelection_jar_aka',
            propertyName: 'preferredLanguage'
          }
        },

        /**
         * @desc toggle ApBlockoutUseLocaleDateFormat
         * Get the toggle value with the cookie name
         * @type {Boolean}
         */
        _toggleUseLocaleDateFormat: {
          type: Boolean,
          value: function() {
            return this._getToggleUseLocaleDateFormat();
          }
        },
        /**
         * @desc Supported languages to display in calendar
         * @type {Array}
         */
         supportedLanguages: Array
      };
    }

    /**
     * @desc get day difference
     * @param {Date} higherDate
     * @param {Date} lowerDate
     * @return {Number}
     */
     getDateDifference(higherDate, lowerDate) {
      let timeDiff = Math.abs(higherDate.getTime() - lowerDate.getTime());
      let diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
      return diffDays;
    }

    /**
     * @desc get date from cookie || date today
     * note: checks to see if date is valid if coming from the cookie
     * @see https://stackoverflow.com/a/1353711
     * @return {Date} current date
     */
    getToday() {
      let systemTime = Cookies.get('setSystemTime');
      if (systemTime) {
        let systemTimeDate = new Date(systemTime),
          isValidDate = Object.prototype.toString.call(systemTimeDate) === "[object Date]" && !isNaN(systemTimeDate.getTime());

        if (isValidDate) {
          return systemTimeDate;
        }
      }

      return new Date();
    }

    /**
     * @desc determine if date is in the past (before start date)
     * @param {String} date, ex: 2017-10-20
     * @param {String} start date, ex: 2017-10-20
     * @return {Boolean}
     */
    isPastDate(date, startDate) {
      if (!date || !startDate) return true;
      // get time in milliseconds and round it off to the number of days
      const dateObject = this.getDateObject(date, false, this._toggleUseLocaleDateFormat);
      let dateTimestamp = parseInt(dateObject.getTime() / (1000 * 3600 * 24));
      const startDateObject = this.getDateObject(startDate, false, this._toggleUseLocaleDateFormat);
      let startDateTimestamp = parseInt(startDateObject.getTime() / (1000 * 3600 * 24));
      return dateTimestamp < startDateTimestamp;
    }

    /**
     * @desc compute startDate based on startDate using an ISO8601 formatted string
     * @param {String} start date, ex: 2017-10-20
     * @param {Boolean} forceUTCMidnight
     * @return {Date} start date
     */
    getDateObject(dateString, forceUTCMidnight = false, toggleUseLocaleDateFormat = false) {

      // if time not present, add default time
      if (!!dateString) {
        if (dateString.length === 10) {
          dateString += 'T00:00:00';
        } else {
          dateString = dateString.split('T')[0] + 'T00:00:00';
        }
        if (forceUTCMidnight) {
          dateString += 'Z';
        }
      }

      let returnDate;

      if (!forceUTCMidnight && !!dateString) {

        // use cached date string if available
        if (_comCalendarDateCache[dateString]) {
          return new Date(_comCalendarDateCache[dateString]);
        }

        // create new date for current date string
        returnDate = new Date(dateString);

        let dateStringToCreate = dateString;

        let timeOffset = returnDate.toLocaleTimeString({}, { hour12: false }).replace(/[^ -~]/g, '');
        // Workaround to fix IOS returning UTC time when new Date() is created
        if (timeOffset !== '00:00:00' && timeOffset !== '0:00:00') {

          // get locale date to check how time offset needs to be calculated
          const locale = this._toggleUseLocaleDateFormat ? 'en-US' : {};
          let dateCompare = returnDate.toLocaleDateString(locale, { month: '2-digit' }) + '-'
            + returnDate.toLocaleDateString(locale, { day: '2-digit' }) + '-'
            + returnDate.toLocaleDateString(locale, { year: 'numeric'});

          let matched = dateCompare.match(/([0-9]{2})\-([0-9]{2})\-([0-9]{4})/);

          // check to see if dateCompare is in MM-DD-YYYY and dateString is in YYYY-MM-DD
          if (
            matched &&
            dateString.match(/([0-9]{4})\-([0-9]{2})\-([0-9]{2})/)
          ) {
            dateCompare = matched[3] + '-' + matched[1] + '-' + matched[2];
          }

          timeOffset = parseInt(timeOffset);
          let offsetOperator = '+';
          // if date is previous date
          if (!!dateString && dateCompare + 'T00:00:00' !== dateString) {
            if (dateCompare + 'T00:00:00' < dateString) {
              timeOffset = 24 - timeOffset;
              offsetOperator = '-';
            }

            timeOffset = (timeOffset <= 9 ? '0' : '') + timeOffset + ':00';
            dateStringToCreate = dateString + offsetOperator + timeOffset;
            returnDate = new Date(dateStringToCreate);
          }
        }
        // cache string used to create the date
        _comCalendarDateCache[dateString] = dateStringToCreate;
      } else {
        // update to return today if datestring is null
        returnDate = !!dateString ? new Date(dateString) : this.getToday();
      }
      return returnDate;
    }

    /**
     * @desc get days of the week based on PST timezone with locale formatting
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleString
     * @param {String} locale
     * @param {String} presentation
     * @return {String[]} weekdays
     */
    getWeekdays(locale, presentation, weekdayOffset) {
      if (!this._useToLocaleString) {
        return (presentation === 'compact') ? this._shortDays : this._days;
      }
      let weekdays = [],
        pstOffset = 480,
        date = new Date(),
        utc = date.getTime() + (date.getTimezoneOffset() * 60000),
        pstDate = new Date(utc + (3600000 * pstOffset));

      for (let i = 0; i < 7; i++) {
        let weekdayType = (presentation === 'compact') ? 'narrow' : 'short',
          weekdayAbbreviation = pstDate.toLocaleString(locale, { weekday: weekdayType }),
          weekdayFull = pstDate.toLocaleString(locale, { weekday: 'long' });

        // fix for IE11 since it's implementation of toLocaleString returns
        // different format for narrow option
        if (presentation === 'compact' && weekdayAbbreviation.length > 1) {
          weekdayAbbreviation = weekdayAbbreviation.substr(1, 1);
        }
        if (weekdayOffset !== 0 && pstDate.getDay() === 0) {
          weekdays[7 - weekdayOffset] = {
            abbreviation: weekdayAbbreviation,
            full: weekdayFull
          }
        }
        weekdays[pstDate.getDay() - weekdayOffset ] = {
          abbreviation: weekdayAbbreviation,
          full: weekdayFull
        };
        pstDate.setDate(pstDate.getDate() + 1);
      }

      return weekdays;
    }

    getMonthName(date, locale) {
      if (!this._useToLocaleString) {
        return this._months[date.getMonth()];
      }

      return date.toLocaleString(locale, {month: 'long'});
    }

    /**
     * @desc Helper function to get only the date portion of an ISO date string
     * @param {Date} date
     * @return {String} date, ex: 2017-10-20
     */
    getStringFromDate(date) {
      return date.getFullYear() + '-' +
        this._getZeroPadding(date.getMonth() + 1) + '-' +
        this._getZeroPadding(date.getDate());
    }

    /**
     * @desc Helper function to get the full string of a date
     * @param {Date} date
     * @param {String} country/language code
     * @param {String} monthFormat string
     * @return {String} date, ex: January 13, 2018
     */
    getFullStringFromDate(date, locale, {
      monthFormat = 'long'
    } = {}){
      return date.toLocaleString(locale, { month: monthFormat }) + ' ' +
        date.getDate() + ', ' +
        date.getFullYear();
    }

    /**
     * @desc get zero padding for number
     * @param {Number} string or number
     * @return {String} number
     */
    _getZeroPadding(number) {
      if (typeof number === 'number') {
        number = (number.toString());
      }
      number = (number.length < 2) ? '0' + number : number;
      return number;
    }

    /**
     * @desc check support for Date.toLocaleString
     * @return {Boolean}
     */
    _toLocaleStringSupportsLocales() {
      try {
        new Date().toLocaleString('i');
      } catch (e) {
        return e.name === 'RangeError';
      }
      return false;
    }

    /**
     * @desc get the day of the week on which the current month falls
     * @param {Date} startDate
     * @return {Date} first day of month
     */
    _getFirstDay(startDate) {
      let firstDay = new Date(startDate.getFullYear(), startDate.getMonth(), 1).getDay();
      return firstDay;
    }

    /**
     * @desc get the first date of the month
     * @param {Date} date
     * @return {String} firstDate, e.g. 2018-03-01
     */
    getFirstDateOfTheMonth(date) {
      let dateCopy = this.getDateObject(date);
      dateCopy.setDate(1);
      dateCopy.setMonth(dateCopy.getMonth());
      return this.getStringFromDate(dateCopy);
    }

    /**
     * @desc get the number of days in the month
     * @param {Date} startDate
     * @return {Number} numberOfDays
     */
    _getNumberOfDays(startDate) {
      // overflow the Javascript Date prototype to calculate the number of days in the current month
      // i.e. for 02/2016:  new Date(2016, 1, 32) = Thu Mar 03 2016 00:00:00 GMT-0800 (PST)
      // new Date(2016, 1, 32).getDate() = 3
      // 32 - 3 = 29 (29 days for the month 2/2016)

      let numberOfDays = 32 - new Date(startDate.getFullYear(), startDate.getMonth(), 32).getDate();
      return numberOfDays;
    }

    /**
     * @desc `getLocale` // en-us, es-ar based on localeCookie_jar_aka or marketing_code
     * assumption is that there can never be both cookies available at the same time
     * @return {String} locale
     */
     getLocale() {
      let localeCookie = Cookies.get(this._localeFields.cookieName),
        marketingCodeCookie = Cookies.get('marketing_code'),
        locale;

      locale = (localeCookie) ?
        JSON.parse(localeCookie)[this._localeFields.propertyName].replace(/_/, '-').toLowerCase() :
        'en-us';
      // for DLP mvp we are only using suppororted languages and English as default
      if (marketingCodeCookie) {
        locale = 'en-gb';

        if (this.supportedLanguages) {
          locale = (this.supportedLanguages.indexOf(marketingCodeCookie) !== -1) ? marketingCodeCookie : locale;
        }
      }

      return locale;
    }

    /**
     * @desc `_getToggleUseLocaleDateFormat` // get toggle value apBlockoutUseLocaleDateFormat
     * @return Boolean
     */
    _getToggleUseLocaleDateFormat() {
      return false;
      // let pepConfig = document.createElement('dprd-pep-config');

      // return !!pepConfig.getConfig('Toggles.Release.apBlockoutUseLocaleDateFormat');
    }

    /**
     * @desc determine if currentDate and startDate are the same (compare year, month and day)
     * @param {String} currentDate ex: 2017-10-20
     * @param {String} startDate ex: 2017-10-20
     * @param {Boolean} compareDay
     * @return {Boolean}
     */
    isSameDate(currentDate, startDate, compareDay) {
      if (!currentDate || !startDate) return false;
      const areMonthsEqual = this.getDateObject(currentDate).getMonth() === this.getDateObject(startDate).getMonth();
      const areYearsEqual = this.getDateObject(currentDate).getFullYear() === this.getDateObject(startDate).getFullYear();

      let isCurrentDateSameDate = areYearsEqual && areMonthsEqual;

      if (compareDay) {
        const areDaysEqual = this.getDateObject(currentDate).getDate() === this.getDateObject(startDate).getDate();
        isCurrentDateSameDate = isCurrentDateSameDate && areDaysEqual;
      }

      return isCurrentDateSameDate;
    }
  }
}
